/* eslint-disable no-restricted-imports */
/* eslint-disable react/forbid-component-props */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import {
	ProductSizeInput,
	ProductSizeSelectSx,
	titleSX,
} from '@/components/content/ProductDetails/parts/CompleteLook/style/completeLook';
import { useMerchandisingAssociation } from '@/data/Content/MerchandisingAssociation';
import {
	Box,
	Checkbox,
	Divider,
	MenuItem,
	Select,
	SelectChangeEvent,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import React, {
	FC,
	useCallback,
	useEffect,
	useState,
	MouseEvent,
	useMemo,
	useContext,
} from 'react';
import { ID, TransactionErrorResponse } from '@/data/types/Basic';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { getAttrsByIdentifier, useProductDetails } from '@/data/Content/ProductDetails';
import { ProductImage } from '@/components/blocks/ProductImage';
import {
	ProductDisplayPrice,
	ProductType,
	ResponseProductAttributeValue,
	Ribbon,
} from '@/data/types/Product';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Linkable } from '@/components/blocks/Linkable';
import { ResponseProductAttribute } from '@/data/types/Product';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { PricesProduct } from '@/components/content/ProductDetails/parts/CompleteLook/parts/PricesProduct';
import { OneClick } from '@/components/blocks/OneClick';
// eslint-disable-next-line no-restricted-imports
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import { PriceDisplay } from '@/components/blocks/PriceDisplay';
// eslint-disable-next-line no-restricted-imports
import { mapProductDetailsData, search } from '@/data/utils/mapProductDetailsData';
import { STRING_TRUE } from '@/data/constants/catalog';
import { useProduct } from '@/data/Content/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { addToCartFetcher, BASE_ADD_2_CART_BODY } from '@/data/Content/Cart';
import { mutate } from 'swr';
// eslint-disable-next-line no-restricted-imports
import { cartMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/cartMutatorKeyMatcher';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { useSettings } from '@/data/Settings';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNotifications } from '@/data/Content/Notifications';
// eslint-disable-next-line no-restricted-imports
import { processError } from '@/data/utils/processError';
import { useInventory, useInventoryArray } from '@/data/Content/_Inventory';
import { useAllowableShippingModes } from '@/data/Content/_AllowableShippingModes';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { isExcludeRibbon } from '@/utils/mergeRibbonsForProduct';
import { EventsContext } from '@/data/context/events';

type ProductWithSizes = ProductType & {
	sizesValues?: ResponseProductAttributeValue[];
	selectedSizeValue?: string;
	availability?: ProductAvailabilityData[] | undefined;
};

type ProductsSKU =
	| {
			buyable: boolean;
			sku: ProductWithSizes;
			attrsByIdentifier: Record<string, string> | undefined;
			quantity?: number | undefined;
	  }
	| undefined;

export const CompleteLook: FC<{
	id: ID;
	// eslint-disable-next-line complexity
}> = ({ id }) => {
	const { settings } = useSettings();
	const params = useExtraRequestParameters();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { storeLocator } = useStoreLocatorState();
	const [physicalStoreName, setPhysicalStoreName] = useState<string>('');
	const productDetails = useProductDetails({ partNumber: id.toString(), physicalStoreName });
	const { product } = productDetails;
	const [selectedItems, setSelectedItems] = useState<ProductWithSizes[]>([]);
	const [totalAmount, setTotalAmount] = useState<number>(0);
	const [disabledButton, setDisabledButton] = useState<boolean>(true);
	const { onMultiAddToCart } = useContext(EventsContext);

	const merchandisingAssociation = useMerchandisingAssociation(id);

	const merchAssocs = useMemo(() => {
		if (!product || !merchandisingAssociation.merchAssocs.length) {
			return [];
		}

		const merchandisingAssociationFiltered = merchandisingAssociation.merchAssocs.filter(
			(item) => item.associationType === 'X-SELL'
		);

		return [product, ...merchandisingAssociationFiltered];
	}, [merchandisingAssociation.merchAssocs, product]);

	const merchAssocsPartNumbers = useMemo(() => {
		if (!merchAssocs.length) {
			return [];
		}

		return merchAssocs
			.map((productItem) => {
				const items = productItem.items;

				return items.map((item) => item.partNumber);
			})
			.flat();
	}, [merchAssocs]);

	const { availability: avai } = useInventoryArray(merchAssocsPartNumbers, physicalStoreName);

	const [items, setItems] = useState([] as ProductWithSizes[]);
	const urlBaseCloudfront = 'https://dpjye2wk9gi5z.cloudfront.net';

	const [partNumber, setPartNumber] = useState<string>('');
	const [sizeSelect, setSizeSelect] = useState<string>('');
	const [itemProduct, setItemProduct] = useState<ProductWithSizes | undefined>(undefined);
	const [itemsCart, setItemsCart] = useState([] as ProductsSKU[]);

	// Mover los hooks a nivel del componente
	const { product: inputCE } = useProduct({ id: partNumber });
	const { product: root } = useProduct({
		id: inputCE?.parentCatalogEntryID,
		isCEId: true,
		condition: inputCE?.type === 'item',
	});
	const { selection: initial } = mapProductDetailsData(inputCE, root);
	const { pickupInStoreShipMode } = useAllowableShippingModes();
	const [itemSelectSKU, setItemSelectSKU] = useState<ProductType | undefined>(undefined);
	const { notifyError, showAddToCart } = useNotifications();
	const { availability } = useInventory(itemSelectSKU?.partNumber, physicalStoreName);
	const memoizedAvailability = useMemo(() => availability, [availability]);
	const memoizedItems = useMemo(() => items, [items]);

	const getSizesProduct = (definingAttributes: ResponseProductAttribute[]) => {
		const size = definingAttributes?.find((attr) => attr.identifier === 'Talla');

		const sizesValue = size?.values || [];

		return sizesValue;
	};

	const onSelectChange = (event: SelectChangeEvent, productSelect: ProductWithSizes) => {
		const valueSelect = event.target.value as string;
		setItems((prevItems) =>
			prevItems.map((item) =>
				item.partNumber === productSelect.partNumber
					? { ...item, selectedSizeValue: valueSelect }
					: item
			)
		);

		setPartNumber(productSelect.partNumber);
		setSizeSelect(valueSelect);
		setItemProduct(productSelect);
	};

	const handleCheckboxChange = (product: ProductWithSizes, isChecked: boolean) => {
		setSelectedItems((prevSelectedItems) => {
			const newSelectedItems = isChecked
				? [...prevSelectedItems, product]
				: prevSelectedItems.filter((item) => item.partNumber !== product.partNumber);

			const totalAmount = newSelectedItems.reduce(
				(acc, item) => acc + getPrice(item.productPrice, item.ribbons),
				0
			);

			setTotalAmount(totalAmount);

			return newSelectedItems;
		});
	};

	const getPrice = (productPrice: ProductDisplayPrice, ribborns: Ribbon[]) => {
		const priceList = productPrice.list || 0;
		const priceOffer = productPrice.offer || 0;

		const { price, priceWithoutOffer } = calculateProductPrices({
			list: priceList,
			offer: priceOffer,
			ribbons: ribborns ?? [],
		});

		return (priceOffer > 0 && priceOffer === priceList) ||
			(priceOffer > 0 && priceOffer < priceList)
			? price
			: priceWithoutOffer;
	};

	const validateIssuePartNumber = (partNumber: string) => {
		const sizes = items.map((row) => {
			if (partNumber === row.partNumber) {
				return row.selectedSizeValue;
			}
		});

		const size = sizes.filter((size) => size !== undefined);

		return size[0];
	};

	const onClickAddCart = useCallback(
		async (event: MouseEvent<HTMLElement>) => {
			event.preventDefault();
			event.stopPropagation();

			const orderItem2 = itemsCart
				.filter((item) =>
					// Filtrar solo los elementos de itemsCart cuyos partNumber coincidan con alguno en selectedItems
					selectedItems.some(
						(selectedItem) => item && item.sku.partNumber.startsWith(selectedItem.partNumber)
					)
				)
				.map((item) => {
					// Calcular el availability para cada item
					let avail = item?.sku?.availability?.find((a) => a.status);
					if (!avail) {
						avail = item?.sku?.availability?.find((a) => a.physicalStoreStatus);
					}

					// Crear el nuevo objeto con la estructura deseada
					return {
						productId: item?.sku?.id,
						partNumber: item?.sku?.partNumber,
						quantity: item?.quantity?.toString() || '1',
						...(avail?.physicalStoreId && {
							physicalStoreId: avail.physicalStoreId,
							shipModeId: pickupInStoreShipMode?.shipModeId,
						}),
					};
				});

			const data = { ...BASE_ADD_2_CART_BODY, orderItem: orderItem2 };

			try {
				await addToCartFetcher(true)(settings?.storeId ?? '', {}, data, params);

				const products = itemsCart
					.map((_item) => {
						const _product = product?.items.find(
							(item) => item.partNumber === _item?.sku.partNumber
						);
						return _product;
					})
					.filter((item) => item !== undefined) as ProductType[];

				onMultiAddToCart({
					gtm: {
						from_look: 'true',
						products,
						orgName: '', // TODO: specify selected org-name
						orgId: '', // TODO: specify selected org
						storeName: settings.storeName,
						settings,
						availability,
					},
				});

				await mutate(cartMutatorKeyMatcher(EMPTY_STRING), undefined);
				scrollTo(0, 0);
				showAddToCart();
			} catch (e) {
				const errorParams = (e as TransactionErrorResponse)?.error?.errors?.[0]?.errorParameters;
				const size = validateIssuePartNumber(
					errorParams.split(',').join('-').split('-').slice(0, 2).join('-')
				);

				if ((e as TransactionErrorResponse)?.error.errors[0].errorCode === '2') {
					notifyError(`No hay inventario suficiente para comprar el producto.`);
					return;
				}

				notifyError(processError(e as TransactionErrorResponse));
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			itemsCart,
			notifyError,
			showAddToCart,
			params,
			pickupInStoreShipMode?.shipModeId,
			selectedItems,
			settings?.storeId,
		]
	);

	useEffect(() => {
		setPhysicalStoreName(storeLocator.selectedStore?.physicalStoreName ?? '');
	}, [storeLocator.selectedStore]);

	useEffect(() => {
		if (merchAssocs.length && avai?.length) {
			const updatedMerchAssocs = merchAssocs.map((item) => {
				const newItems = item.items.filter((innerProduct) => {
					const innerProductAvai = avai.find(
						(innerAvai) => innerAvai.productId === innerProduct.id
					);

					if (innerProductAvai?.inventoryStatus !== 'Unavailable') {
						return innerProduct;
					}

					return null;
				});

				return {
					...item,
					items: newItems,
				} as ProductType;
			});

			const updatedItems = updatedMerchAssocs
				.map((item) => ({
					...item,
					fullImage: `${urlBaseCloudfront}${item.thumbnail
						.replace('hclstore', 'wcsstore')
						.replace('thumbnail', 'default')}`,
					ribbons: item.ribbons.filter((e) => !isExcludeRibbon(e.identifier)),
					sizesValues: getSizesProduct(item?.definingAttributes).filter((innerItem) => {
						const innerProductExists = item.items.find((innerProduct) => {
							const size = findProductAttribute(innerProduct.attributes, 'Talla');
							return size?.value === innerItem.value;
						});

						return innerProductExists ? innerItem : null;
					}),
					selectedSizeValue: 'talla',
				}))
				.filter((item) => item.items.length > 0)
				.filter((item) => item.sizesValues?.length > 0);

			setItems(updatedItems);
			setSelectedItems(updatedItems);

			// Calcular el total inicial
			const initialTotal = updatedItems.reduce(
				(acc, item) => acc + getPrice(item.productPrice, item.ribbons),
				0
			);
			setTotalAmount(initialTotal);
		}
	}, [merchAssocs, product, avai]);

	useEffect(() => {
		const allUnchecked = selectedItems.length === 0;

		let shouldDisableButton = true;

		if (!allUnchecked) {
			shouldDisableButton = !selectedItems.every((elm) => {
				const findItem = items.find((item) => item.partNumber === elm.partNumber);
				return findItem && findItem.selectedSizeValue && findItem.selectedSizeValue !== 'talla';
			});
		}

		if (shouldDisableButton !== disabledButton) {
			setDisabledButton(shouldDisableButton);
		}
	}, [selectedItems, items, disabledButton]);

	const onAttributeChange = useCallback(
		(identifier: string, value: string, product: ProductWithSizes) => {
			if (product?.items) {
				const asAttrs = product.items.map((s) => getAttrsByIdentifier(s.definingAttributes));

				const fullAttr = {
					...initial?.attrsByIdentifier,
					[identifier]: value,
				};

				const { exact, best } = search(fullAttr, identifier, asAttrs);

				let sku: ProductType | undefined = undefined;
				let attrsByIdentifier: Record<string, string> | undefined = undefined;

				if (exact !== -1) {
					attrsByIdentifier = fullAttr;
					sku = product.items[exact];
				} else if (best !== -1) {
					attrsByIdentifier = { ...asAttrs[best] };
					sku = product.items[best];
				} else {
					sku = {} as ProductType;
				}
				return {
					...initial,
					buyable: !!(sku?.buyable === STRING_TRUE),
					sku,
					attrsByIdentifier,
				};
			}
		},
		[initial]
	);

	useEffect(() => {
		// Extraemos la lógica para obtener el SKU y evitar depender de onAttributeChange
		const obtenerSKUSeleccionado = () => {
			if (itemProduct) {
				return onAttributeChange('Talla', sizeSelect, itemProduct);
			}
			return null;
		};

		const selectItemSKU = obtenerSKUSeleccionado();
		const itemFind = memoizedItems.find((elm) => elm.partNumber === itemProduct?.partNumber);

		if (
			itemProduct &&
			itemFind &&
			itemFind.selectedSizeValue !== 'talla' &&
			selectItemSKU?.sku?.partNumber
		) {
			setItemSelectSKU((prev) => {
				if (prev?.partNumber !== selectItemSKU.sku.partNumber) {
					return selectItemSKU.sku;
				}
				return prev;
			});

			setItemsCart((prevItems) => {
				const parentPartNumber = (partNumber: string) =>
					partNumber.split('-').slice(0, 2).join('-');

				const exists = prevItems.some(
					(item) =>
						item &&
						parentPartNumber(item.sku.partNumber) ===
							parentPartNumber(selectItemSKU?.sku?.partNumber || '')
				);

				const updatedSKU = {
					...selectItemSKU,
					sku: {
						...selectItemSKU.sku,
						availability: memoizedAvailability,
					},
				};

				if (exists) {
					const newItems = prevItems.map((item) =>
						item &&
						parentPartNumber(item.sku.partNumber) ===
							parentPartNumber(selectItemSKU?.sku?.partNumber || '')
							? updatedSKU
							: item
					);
					if (JSON.stringify(newItems) !== JSON.stringify(prevItems)) {
						return newItems;
					}
				} else {
					return [...prevItems, updatedSKU];
				}

				return prevItems;
			});
		}
		// Minimizar las dependencias a lo esencial
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [itemProduct, sizeSelect, memoizedItems, memoizedAvailability]);

	if (items.length === 1) {
		return null;
	}

	if (merchAssocs.length === 0 || items.length === 0) {
		return null;
	}

	return (
		<>
			<Divider sx={{ marginTop: isMobile ? '20px !important' : '0px !important' }} />
			<Stack
				direction="column"
				spacing={2}
				sx={{
					mt: isMobile ? '21px !important' : '38px !important',
					mb: isMobile ? '44px !important' : '38px !important',
				}}
			>
				<Stack>
					<Typography component="h2" data-testid="offer-price" id="offer-price" sx={titleSX}>
						{'COMPLETA TU LOOK'}
					</Typography>
				</Stack>
				<Stack direction={isMobile ? 'column' : 'row'}>
					<Stack
						direction={'row'}
						position="relative"
						mb={isMobile ? '13px' : '0px'}
						sx={{
							'& > :last-child': {
								marginRight: 0,
							},
						}}
					>
						{/* Contenedor 1 */}
						{items.map((row, index) => (
							<Linkable
								key={row.partNumber}
								href={row?.seo?.href}
								className="complete-look-linkable"
							>
								<Stack
									key={row.partNumber}
									direction="row"
									alignItems={'center'}
									position="relative"
									mr={index === 2 ? 0 : '6px'}
									className="complete-look-item"
								>
									<ProductImage
										sx={{
											mt: '5px !important',
											width: '207px',
											'@media(min-width:960px)': {
												maxWidth: '207px',
												height: '208px',
											},
										}}
										src={row.fullImage}
										alt={'mm'}
										ribbons={row.ribbons}
										isThumbnail={false}
										isSelected={false}
										isCompleteLook={true}
									/>
									{index !== items.length - 1 ? (
										<Box
											sx={{
												position: 'absolute',
												top: '50%',
												left: '100%',
												transform: 'translate(-50%, -50%)',
												zIndex: 2,
												marginLeft: '3px',
											}}
										>
											<AddCircleIcon
												sx={{
													fontSize: 'var(--font-size-xxl)',
													marginTop: '10px',
													'@media(min-width:960px)': {
														fontSize: 'var(--font-size-display)',
														marginTop: '0px',
													},
												}}
											/>
										</Box>
									) : null}
								</Stack>
							</Linkable>
						))}
					</Stack>
					<Stack direction={'column'} sx={{ p: isMobile ? '0px' : '0px 35px 0px 10px' }}>
						{/* Contenedor 2 */}
						<Typography
							component={'p'}
							sx={{ color: '#2c2c2c', font: 'normal normal normal 9px Open Sans', pl: '44px' }}
						>
							{'ESTE PRODUCTO'}
						</Typography>
						{/* eslint-disable-next-line complexity*/}
						{items.map((row) => (
							<Stack key={row.partNumber} mb={isMobile ? 2 : 1} direction={'row'}>
								{/* Contenedor principal */}
								<Stack direction={'column'}>
									{/* Contenedor checkbox */}
									<Checkbox
										checked={selectedItems.some((item) => item.partNumber === row.partNumber)}
										onChange={(event) => handleCheckboxChange(row, event.target.checked)}
										sx={{ px: '10px', py: 0 }}
									/>
								</Stack>
								<Stack direction={'column'} width={'100%'}>
									{/* Contenedor Nombre del articulo, precios , Dropdown talla */}
									<Stack direction={'row'}>
										{/* Contenedor Nombre del articulo */}
										<Typography sx={{ fontSize: 'var(--font-size-small)', fontWeight: '600' }}>
											{row?.name}
										</Typography>
										{/* Nombre del articulo */}
									</Stack>
									<Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
										{/* Contenedor precios */}
										<PricesProduct productItem={row} />

										{avai && avai.length !== 0 ? (
											<Stack direction={'row'}>
												{/* Contenedor Dropdown de Talla*/}
												{row?.sizesValues && row?.sizesValues.length ? (
													<Select
														value={row.selectedSizeValue || 'talla'}
														input={<ProductSizeInput />}
														IconComponent={KeyboardArrowDownIcon}
														sx={ProductSizeSelectSx}
														onChange={(event) => onSelectChange(event, row)}
													>
														<MenuItem disabled value="talla">
															{'Talla'}
														</MenuItem>
														{row?.sizesValues?.map((item, index) => (
															<MenuItem key={index} value={item.identifier}>
																{item.identifier}
															</MenuItem>
														))}
													</Select>
												) : null}
											</Stack>
										) : (
											<Stack direction={'row'}>
												{/* Contenedor Dropdown de Talla*/}
												{row?.sizesValues && row?.sizesValues.length ? (
													<Select
														value={row.selectedSizeValue || 'talla'}
														input={<ProductSizeInput />}
														IconComponent={KeyboardArrowDownIcon}
														sx={ProductSizeSelectSx}
														onChange={(event) => onSelectChange(event, row)}
													>
														<MenuItem disabled value="talla">
															{'Talla'}
														</MenuItem>
														{row?.sizesValues?.map((item, index) => (
															<MenuItem key={index} value={item.identifier}>
																{item.identifier}
															</MenuItem>
														))}
													</Select>
												) : null}
											</Stack>
										)}
									</Stack>
								</Stack>
							</Stack>
						))}
					</Stack>
					{!isMobile ? <Divider orientation="vertical" sx={{ mr: '29px' }} flexItem /> : null}
					<Stack
						display={'flex'}
						direction={'column'}
						justifyContent={'center'}
						alignItems={'center'}
					>
						{!isMobile ? (
							<>
								<Stack direction={'row'}>
									<Typography
										sx={{
											fontSize: 'var(--font-size-body)',
											color: '#2C2C2C',
											fontWeight: 'normal',
										}}
									>
										{'Cómpralos juntos:'}
									</Typography>
								</Stack>
								<Stack direction={'row'} mt={'6px'}>
									<Typography
										sx={{ fontSize: 'var(--font-size-xl)', color: '#2C2C2C', fontWeight: 'bold' }}
									>
										<PriceDisplay currency={'MXN'} min={totalAmount} />
									</Typography>
								</Stack>
							</>
						) : null}
						<Stack
							direction={'row'}
							mt={'20px'}
							sx={{
								width: { md: '230px !important', xs: '100%' },
								maxWidth: { md: '230px !important', xs: '100%' },
							}}
						>
							<OneClick
								// id={dtId}
								onClick={onClickAddCart}
								variant="contained"
								disabled={disabledButton}
								sx={{ height: '40px' }}
							>
								{!isMobile ? (
									'Agregar al carrito'
								) : (
									<Typography
										sx={{
											fontSize: { md: 'var(--font-size-xl)', xs: 'var(--font-size-sm)' },
											color: { md: '#2C2C2C', xs: '#FFFFFF' },
											fontWeight: 'bold',
										}}
									>
										{'Cómpralos juntos:'} <PriceDisplay currency={'MXN'} min={totalAmount} />
									</Typography>
								)}
							</OneClick>
						</Stack>
					</Stack>
				</Stack>
			</Stack>
			{isMobile ? <Divider sx={{ marginTop: '0px !important' }} /> : null}
		</>
	);
};
